import { LargeSiteAdvancedSettingsRefs } from "@/tools/create/lss-from-map/components/LargeSiteAdvancedSettings";
import { MutableRefObject, PropsWithChildren, createContext, useCallback, useContext, useRef, useState } from "react";

/**
 * LargeSiteScreenTutorial (this tour) is rendered as a SIBLING of PortfolioExportTool, not a
 * descendant — so it cannot call usePortfolioTutorialsState() directly and reach the real
 * PortfolioTutorialProvider (it would only ever see that hook's disconnected no-provider fallback).
 * LargeSiteScreenTutorialBridge.tsx *is* correctly nested inside that provider, so it reads the
 * real refs/setters and republishes them here — since refs are plain mutable objects, handing the
 * same ref instance across this context boundary keeps `.current` reads live regardless of tree shape.
 */
export interface PortfolioBridgeState {
  analysisRun: {
    alert: MutableRefObject<null>;
    item: MutableRefObject<null>;
  };
  portfolioItem: {
    actionsMenu: MutableRefObject<null>;
    downloadsMenu: MutableRefObject<null>;
    viewResultsTrigger: MutableRefObject<null>;
  };
  summaryQueueModal: MutableRefObject<null>;
  outputTypeModal: MutableRefObject<null>;
  editModal: MutableRefObject<null>;
  deleteModal: MutableRefObject<null>;
  setShowSummaryModal: (value: boolean) => void;
  handleSummaryStep: () => void;
  handleSummaryGenerated: () => void;
  setShowDownloadDropdown: (value: boolean) => void;
  setSelectedDownloadItemIndex: (value: number | undefined) => void;
  setShowPointLevelResultsModal: (value: boolean) => void;
  setShowMoreDropdown: (value: boolean) => void;
  setShowTutorialEditModal: (value: boolean) => void;
  setShowTutorialDeleteModal: (value: boolean) => void;
}
interface LssTutorialState {
  isTutorialOpen: boolean;
  // Leg 1: list page, pre-navigation
  startNewRunBtn: MutableRefObject<null>;
  analysisLibrary: MutableRefObject<null>;
  libraryFilters: MutableRefObject<null>;
  // Leg 3: list page, post-handoff review modal
  reviewSection: MutableRefObject<null>;
  usageSection: MutableRefObject<null>;
  runAnalysisBtn: MutableRefObject<null>;
  advancedSettingsRefs: LargeSiteAdvancedSettingsRefs | null;
  setAdvancedSettingsRefs: (refs: LargeSiteAdvancedSettingsRefs) => void;
  defaultAdvancedSettingsOpen: boolean;
  setDefaultAdvancedSettingsOpen: (value: boolean) => void;
  // Mocked table row, driven by the tour and mirrored into Portfolio's tutorial state via a bridge.
  showTutorialProcessingRow: boolean;
  setShowTutorialProcessingRow: (value: boolean) => void;
  showTutorialCompletedRow: boolean;
  setShowTutorialCompletedRow: (value: boolean) => void;
  // Lets the tour close/reopen the review modal, which is owned by a sibling component
  // (LargeSiteScreenTableHeader) - reopening is used when navigating backward from "Processing
  // Your Analysis" to "Run Analysis," since closing the modal unmounts the Run Analysis button.
  closeReviewModal: () => void;
  registerCloseReviewModal: (fn: () => void) => void;
  openReviewModal: () => void;
  registerOpenReviewModal: (fn: () => void) => void;
  // The real PortfolioTutorialContext refs/setters, republished by LargeSiteScreenTutorialBridge
  // (see PortfolioBridgeState doc comment above for why this indirection exists).
  portfolioBridge: PortfolioBridgeState | null;
  setPortfolioBridge: (state: PortfolioBridgeState) => void;
}
const emptyRef: MutableRefObject<null> = {
  current: null
};
const LssTutorialContext = createContext<LssTutorialState | undefined>(undefined);
export const LssTutorialProvider = ({
  isTutorialOpen,
  children
}: PropsWithChildren & {
  isTutorialOpen: boolean;
}) => {
  const startNewRunBtnRef = useRef<null>(null);
  const analysisLibraryRef = useRef<null>(null);
  const libraryFiltersRef = useRef<null>(null);
  const reviewSectionRef = useRef<null>(null);
  const usageSectionRef = useRef<null>(null);
  const runAnalysisBtnRef = useRef<null>(null);
  const [advancedSettingsRefs, setAdvancedSettingsRefs] = useState<LargeSiteAdvancedSettingsRefs | null>(null);
  const [defaultAdvancedSettingsOpen, setDefaultAdvancedSettingsOpen] = useState(false);
  const [showTutorialProcessingRow, setShowTutorialProcessingRow] = useState(false);
  const [showTutorialCompletedRow, setShowTutorialCompletedRow] = useState(false);
  const closeReviewModalRef = useRef<(() => void) | null>(null);
  const registerCloseReviewModal = useCallback((fn: () => void) => {
    closeReviewModalRef.current = fn;
  }, []);
  const closeReviewModal = useCallback(() => {
    closeReviewModalRef.current?.();
  }, []);
  const openReviewModalRef = useRef<(() => void) | null>(null);
  const registerOpenReviewModal = useCallback((fn: () => void) => {
    openReviewModalRef.current = fn;
  }, []);
  const openReviewModal = useCallback(() => {
    openReviewModalRef.current?.();
  }, []);
  const [portfolioBridge, setPortfolioBridge] = useState<PortfolioBridgeState | null>(null);
  return <LssTutorialContext.Provider value={{
    isTutorialOpen,
    startNewRunBtn: startNewRunBtnRef,
    analysisLibrary: analysisLibraryRef,
    libraryFilters: libraryFiltersRef,
    reviewSection: reviewSectionRef,
    usageSection: usageSectionRef,
    runAnalysisBtn: runAnalysisBtnRef,
    advancedSettingsRefs,
    setAdvancedSettingsRefs,
    defaultAdvancedSettingsOpen,
    setDefaultAdvancedSettingsOpen,
    showTutorialProcessingRow,
    setShowTutorialProcessingRow,
    showTutorialCompletedRow,
    setShowTutorialCompletedRow,
    closeReviewModal,
    registerCloseReviewModal,
    openReviewModal,
    registerOpenReviewModal,
    portfolioBridge,
    setPortfolioBridge
  }} data-sentry-element="unknown" data-sentry-component="LssTutorialProvider" data-sentry-source-file="LssTutorialContext.tsx">
      {children}
    </LssTutorialContext.Provider>;
};
export const useLssTutorialState = (): LssTutorialState => {
  const context = useContext(LssTutorialContext);
  if (!context) {
    return {
      isTutorialOpen: false,
      startNewRunBtn: emptyRef,
      analysisLibrary: emptyRef,
      libraryFilters: emptyRef,
      reviewSection: emptyRef,
      usageSection: emptyRef,
      runAnalysisBtn: emptyRef,
      advancedSettingsRefs: null,
      setAdvancedSettingsRefs: () => {},
      defaultAdvancedSettingsOpen: false,
      setDefaultAdvancedSettingsOpen: () => {},
      showTutorialProcessingRow: false,
      setShowTutorialProcessingRow: () => {},
      showTutorialCompletedRow: false,
      setShowTutorialCompletedRow: () => {},
      closeReviewModal: () => {},
      registerCloseReviewModal: () => {},
      openReviewModal: () => {},
      registerOpenReviewModal: () => {},
      portfolioBridge: null,
      setPortfolioBridge: () => {}
    };
  }
  return context;
};